// Detail drawer for Order Intake review (OI-3 + OI-6a + OI-6b).
// Shared by the Ops-reviewer salesIntake variant and the Mfg-accepter panel.

import { useMemo } from "react";

import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingOrderIntakePayloadDiff } from "@/components/manufacturing/ManufacturingOrderIntakePayloadDiff";
import {
  formatIntakeDateTime,
  ghostBtnClass,
  KIND_LABELS,
  SOURCE_LABELS,
} from "@/components/manufacturing/ManufacturingOrderIntakeShared";
import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";

import { OrderIntakeReviewActions } from "./OrderIntakeReviewActions";
import { IntakePaperworkChecklist } from "./IntakePaperworkChecklist";
import { IntakeReviewPartiesPanel } from "./IntakeReviewPartiesPanel";
import {
  buildIntakePaperworkChecks,
  intakePaperworkReadyForAccept,
  readIntakeCustomerContact,
} from "./opsIntakePaperworkCheck";
import { extractIntakeReviewParties } from "@/lib/ops/intakeReviewParties";

export type OrderIntakeReviewVariant = "ops_reviewer" | "mfg_accepter";

export type OrderIntakeReviewDetailDrawerProps = {
  row: OpsOrderIntakeRow;
  variant: OrderIntakeReviewVariant;
  onClose: () => void;
  actionBusy: boolean;
  actionError: string | null;
  onAccept: (plantOverride: string | null) => Promise<boolean>;
  onHold: (reason: string) => Promise<boolean>;
  onCancel: () => Promise<boolean>;
  onReturnToHold: (reason: string) => Promise<boolean>;
};

export function OrderIntakeReviewDetailDrawer({
  row,
  variant,
  onClose,
  actionBusy,
  actionError,
  onAccept,
  onHold,
  onCancel,
  onReturnToHold,
}: OrderIntakeReviewDetailDrawerProps) {
  const isOps = variant === "ops_reviewer";

  const proposedFields = useMemo(() => buildProposedFieldList(row), [row]);
  const paperworkChecks = useMemo(
    () => (isOps ? buildIntakePaperworkChecks(row) : []),
    [isOps, row],
  );
  const paperworkReady = isOps ? intakePaperworkReadyForAccept(paperworkChecks) : true;
  const customerContact = useMemo(() => readIntakeCustomerContact(row), [row]);
  const parties = useMemo(() => extractIntakeReviewParties(row), [row]);

  return (
    <SectionCard
      title={isOps ? "Paperwork review" : "Intake detail"}
      description={`#${row.id} · ${row.status.replace("_", " ")}`}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
        <div className="text-gray-700 dark:text-zinc-300">
          <div>
            <span className="font-medium">Source:</span>{" "}
            {SOURCE_LABELS[row.source] ?? row.source}
          </div>
          <div>
            <span className="font-medium">Kind:</span> {KIND_LABELS[row.kind]}
          </div>
          {row.external_source_id ? (
            <div>
              <span className="font-medium">External id:</span> {row.external_source_id}
            </div>
          ) : null}
          {row.materialized_deal_id ? (
            <div>
              <span className="font-medium">Materialized deal:</span>{" "}
              {row.materialized_deal_id}
            </div>
          ) : null}
        </div>
        <button type="button" className={ghostBtnClass} onClick={onClose} disabled={actionBusy}>
          Close
        </button>
      </div>

      {row.idearoom_correlation ? (
        <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-100">
          <div className="font-semibold">IdeaRoom floor-plan bridge</div>
          <div>Design hash: {row.idearoom_correlation.idearoom_event_hash}</div>
          <div>
            ShedSuite quote: {row.idearoom_correlation.shedsuite_quote_id ?? "Not matched"} · order:{" "}
            {row.idearoom_correlation.shedsuite_order_number ?? row.idearoom_correlation.customer_order_id ?? "Not matched"}
          </div>
          <div>
            Match: {row.idearoom_correlation.match_status.replaceAll("_", " ")} · floor plan:{" "}
            {row.idearoom_correlation.import_status.replaceAll("_", " ")}
          </div>
          {row.idearoom_correlation.import_error ? (
            <div className="text-red-700 dark:text-red-300">
              Retry context: {row.idearoom_correlation.import_error}
            </div>
          ) : null}
        </div>
      ) : null}

      {isOps ? (
        <div className="mt-3 grid items-start gap-4 lg:grid-cols-2">
          <div className="min-w-0 space-y-3">
            <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs">
              {proposedFields.map((f) => (
                <div key={f.label} className="flex justify-between gap-3">
                  <dt className="text-gray-500 dark:text-zinc-400">{f.label}</dt>
                  <dd className="text-right text-gray-800 dark:text-zinc-200">{f.value}</dd>
                </div>
              ))}
            </dl>
            {row.hold_reason ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                <span className="font-medium">Hold reason:</span> {row.hold_reason}
              </div>
            ) : null}
            <IntakePaperworkChecklist items={paperworkChecks} />
            <ManufacturingOrderIntakePayloadDiff row={row} />
          </div>
          <div className="min-w-0 rounded-md border border-gray-200 bg-gray-50/80 px-3 py-3 dark:border-zinc-700 dark:bg-zinc-900/40">
            <IntakeReviewPartiesPanel parties={parties} />
          </div>
        </div>
      ) : (
        <>
          {customerContact ? (
            <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs text-indigo-950 dark:border-indigo-800 dark:bg-indigo-950/30 dark:text-indigo-100">
              <div className="font-semibold">{customerContact.name ?? "Customer"}</div>
              {customerContact.phone ? <div>{customerContact.phone}</div> : null}
              {customerContact.email ? <div>{customerContact.email}</div> : null}
              {customerContact.address ? <div>{customerContact.address}</div> : null}
            </div>
          ) : null}

          <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
            {proposedFields.map((f) => (
              <div key={f.label} className="flex justify-between gap-3">
                <dt className="text-gray-500 dark:text-zinc-400">{f.label}</dt>
                <dd className="text-right text-gray-800 dark:text-zinc-200">{f.value}</dd>
              </div>
            ))}
          </dl>

          {row.hold_reason ? (
            <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
              <span className="font-medium">Hold reason:</span> {row.hold_reason}
            </div>
          ) : null}

          <ManufacturingOrderIntakePayloadDiff row={row} />
        </>
      )}

      {actionError ? (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {actionError}
        </div>
      ) : null}

      <OrderIntakeReviewActions
        row={row}
        variant={variant}
        actionBusy={actionBusy}
        paperworkReady={paperworkReady}
        onAccept={onAccept}
        onHold={onHold}
        onCancel={onCancel}
        onReturnToHold={onReturnToHold}
      />
    </SectionCard>
  );
}

type ProposedField = { label: string; value: string };

function buildProposedFieldList(row: OpsOrderIntakeRow): ProposedField[] {
  const fields: ProposedField[] = [
    { label: "Submitted", value: formatIntakeDateTime(row.created_at) },
    { label: "Last update", value: formatIntakeDateTime(row.updated_at) },
  ];

  if (row.submitted_by_org_id) {
    fields.push({ label: "Submitter org", value: row.submitted_by_org_id });
  }
  if (row.proposed_plant_id) {
    fields.push({ label: "Proposed plant", value: row.proposed_plant_id });
  }
  if (row.proposed_seller_location_id) {
    fields.push({
      label: "Seller location",
      value: row.proposed_seller_location_id,
    });
  }
  if (row.proposed_model_id) {
    fields.push({ label: "Model", value: row.proposed_model_id });
  }
  if (row.proposed_sale_path) {
    fields.push({ label: "Sale path", value: row.proposed_sale_path });
  }
  if (row.proposed_inventory_holder_org_id) {
    fields.push({
      label: "Inventory holder org",
      value: row.proposed_inventory_holder_org_id,
    });
  }
  if (row.proposed_end_customer_contact_id) {
    fields.push({
      label: "End customer contact",
      value: row.proposed_end_customer_contact_id,
    });
  }
  if (row.decided_at) {
    fields.push({ label: "Decided at", value: formatIntakeDateTime(row.decided_at) });
  }
  if (row.cancelled_at) {
    fields.push({ label: "Cancelled at", value: formatIntakeDateTime(row.cancelled_at) });
  }

  return fields;
}
